import { Checkbox } from '@restomarker/ui-kit';
import { useState, type ReactElement } from 'react';

import { t } from '@/shared/i18n';

import { AlertTiersField } from './AlertTiersField';
import type {
  AlertSettings,
  AlertSettingsPatch,
  AlertTier,
  TelegramAlertChannel,
} from './alertSettingsTypes';
import { channelOf, saveErrorText, useUpdateAlertSettings } from './api';
import { pushOffered } from './push';
import { PushSection } from './PushSection';
import { askOnNextConnect } from './pushSync';
import { TelegramSection } from './TelegramSection';

export interface ChannelSectionsProps {
  settings: AlertSettings;
  /** Без сети и в саппорт-сессии: настройки только для чтения. */
  disabled: boolean;
  supportSession: boolean;
}

/**
 * Каналы предупреждений человека по серверному состоянию (T-046): у каждого своя галочка
 * и своя сетка порогов. Что показывать, решает сервер, а не ключ на телефоне (B-039).
 *
 * Запрос — один за раз: пока идёт сохранение, все галочки выключены. Без сети
 * сохранение не стартует вовсе (контролы выключены), поэтому поставленных на паузу
 * запросов, которые молча пропадут при закрытии приложения, не бывает.
 *
 * Телефон окно не подключает: подключение замечает сохранённый выбор само (`usePushSync`)
 * и потому случается, даже если окно закрыли, не дождавшись ответа (ревью B-039).
 */
export function ChannelSections({
  settings,
  disabled,
  supportSession,
}: ChannelSectionsProps): ReactElement {
  const update = useUpdateAlertSettings({
    // Push включили здесь — подключение этого телефона спросит разрешение Android.
    onSaved: (saved, patch) => {
      if (patch.push?.enabled === true && saved.push.enabled) askOnNextConnect();
    },
  });
  // Раздел push, однажды показанный, не пропадает до закрытия окна. На планшете Windows
  // раздел есть только при включённом выборе, и снятая галочка убирала бы его целиком —
  // вместе с возможностью поставить её обратно, если сняли по ошибке. Защёлка, а не
  // значение при открытии: выбор мог включиться, пока окно открыто.
  const [pushSeen, setPushSeen] = useState(settings.push.enabled);
  if (settings.push.enabled && !pushSeen) setPushSeen(true);
  const busy = disabled || update.isPending;
  const failed = update.isError ? channelOf(update.variables) : null;
  const errorText = update.isError ? saveErrorText(update.error, failed) : null;
  const showPush = pushOffered(settings.push, pushSeen);

  function save(patch: AlertSettingsPatch): void {
    update.mutate(patch);
  }

  return (
    <>
      {settings.telegram.available ? (
        <TelegramSection
          showTitle={showPush}
          disabled={disabled}
          settingsConnected={settings.telegram.connected}
          saveError={failed === 'telegram' ? errorText : null}
          alerts={(perChat) => (
            <TelegramAlerts
              telegram={settings.telegram}
              availableTiers={settings.available_tiers}
              disabled={busy}
              perChat={perChat}
              onChange={save}
            />
          )}
        />
      ) : null}
      {showPush ? (
        <PushSection
          push={settings.push}
          availableTiers={settings.available_tiers}
          disabled={busy}
          supportSession={supportSession}
          saveError={failed === 'push' ? errorText : null}
          onEnabledChange={(enabled) => save({ push: { enabled } })}
          onTiersChange={(tiers) => save({ push: { tiers } })}
        />
      ) : null}
    </>
  );
}

interface TelegramAlertsProps {
  telegram: TelegramAlertChannel;
  availableTiers: readonly AlertTier[];
  disabled: boolean;
  /** Сервер знает чаты (T-058): вкл/выкл — галочкой у каждого чата, здесь только пороги. */
  perChat: boolean;
  onChange: (patch: AlertSettingsPatch) => void;
}

/**
 * Предупреждения в Telegram. С T-058 — только пороги, общие для всех чатов учётки: они
 * видны, пока хоть у одного чата включены предупреждения (`enabled`). У сервера старше —
 * галочка канала и пороги под ней.
 */
function TelegramAlerts({
  telegram,
  availableTiers,
  disabled,
  perChat,
  onChange,
}: TelegramAlertsProps): ReactElement {
  return (
    <>
      {perChat ? null : (
        <Checkbox
          checked={telegram.enabled}
          disabled={disabled}
          onCheckedChange={(enabled) => onChange({ telegram: { enabled } })}
          label={t('telegram.alerts')}
          hint={t('telegram.alertsHint')}
        />
      )}
      {telegram.connected && telegram.enabled ? (
        <AlertTiersField
          value={telegram.tiers}
          available={availableTiers}
          disabled={disabled}
          legend={perChat ? t('telegram.whenChats') : t('alerts.whenMine')}
          onChange={(tiers) => onChange({ telegram: { tiers } })}
        />
      ) : null}
    </>
  );
}
